import React, { ReactNode } from 'react';

import { Pie } from './Pie';
import type { PieProps } from './Pie.types';
import { isStaticPieProps } from './Pie.types';
import { PieLegend } from './PieLegend';

/**
 * PieChart Component
 *
 * Composes a title, pie chart, and legend in a single layout.
 *
 * @component
 * @param {PieProps & { title?: string }} props - Props for the pie chart, including data, formatting, and optional title.
 * @returns {JSX.Element}
 */
export const PieChart: React.FC<PieProps & { title?: ReactNode }> = ({
  data,
  className,
  style,
  title,
  tooltipFormat = 'percentage',
  totals,
  ...rest
}) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let width: string | number | undefined;
  let height: number | undefined;
  if (rest.responsive) {
    width = '100%';
    height = 300;
  } else if (
    isStaticPieProps({
      data,
      className,
      style,
      tooltipFormat,
      width: rest.width,
      height: rest.height,
    })
  ) {
    const { width: staticWidth, height: staticHeight } = rest;
    width = staticWidth;
    height = staticHeight;
  }
  // Fallback assignment to ensure width and height are always defined
  if (width === undefined) {
    width = 300;
  }
  if (height === undefined) {
    height = 300;
  }
  return (
    <div className={['pie-chart-container', className].filter(Boolean).join(' ')}>
      {title && <div className="pie-chart-title">{title}</div>}
      <div className="pie-chart-pie" style={{ width, height, ...style }}>
        <Pie
          data={data}
          className={className}
          style={style}
          tooltipFormat={tooltipFormat}
          {...rest}
          aria-label="Pie chart"
          role="img"
        />
      </div>
      <div className="pie-chart-legend">
        <PieLegend data={data} total={total} tooltipFormat={tooltipFormat} />
      </div>
      {totals && <div className="pie-chart-totals">{totals}</div>}
    </div>
  );
};

export default PieChart;
